/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { withWebChat } from '@ibm-watson/assistant-web-chat-react';
import {
  getCustomMessageURL,
  getCustomConfigURL
} from '@ibm-watson/shared/modules/channels/api/web-chat';
import { settingsSchema } from '@ibm-watson/shared/modules/channels/components/web-chat/schema';
import { WEBCHAT_CHANNEL_TYPE } from '@ibm-watson/shared/modules/channels/util/channelTypes';
import { COLOR_REGEX } from '@ibm-watson/shared/modules/channels/util/validation/constants';
import {
  isAws,
  wxoUrl,
  isCpd,
  isWxoEnabled
} from '@ibm-watson/shared/modules/ia/util/config';
import { Header } from '../../../../ts/header';
import { PlanName } from '../../../../ts/plan';
import { endpoints } from '../../../ia/util/config';
import { buildIntegrationsUrlCpd, buildWebchatUrlCpd } from '../urls';

export const NAMESPACES = {
  PREVIEW_PAGE: 'PreviewPage',
  CHANNEL_PAGE: 'ChannelPage'
};

export const LOADING_STATUSES = {
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error'
};

export const TOOLING_TYPES = {
  /**
   * This represents the web chat preview that is displayed within the web chat integration configuration pages.
   */
  WEB_CHAT: 'web-chat-settings',

  /**
   * This is the web chat used to preview your web chat.
   */
  ASSISTANT_PREVIEW: 'assistant-preview',

  /**
   * This is the web chat used for preview when authoring actions.
   */
  ACTIONS: 'actions',

  /**
   * This is the web chat used for preview of a search skill.
   */
  SEARCH: 'search',

  /**
   * This is the web chat used for displaying the conversation history from the logs on the analytics pages.
   */
  ANALYTICS: 'analytics',

  /**
   * This is the web chat used for preview when authoring actions media.
   */
  ACTIONS_MEDIA: 'actions_media'
};

export const CONNECT_TO_AGENT_BEHAVIOR_TYPES = {
  /**
   * This behavior is the same as the error case except that it displays a warning targeted for the authoring user
   * instead of the end user. This is to help her understand that she is missing some configuration.
   */
  WARN_MISSING: 'warning',

  /**
   * This indicates that a mock/preview CTA card should be displayed. This is used in the cases where there is no
   * possibility of a service desk being configured.
   */
  PREVIEW: 'preview'
};

export const getWebchatBaseURL = () => {
  let baseUrl = null;
  if (isCpd) {
    baseUrl = buildWebchatUrlCpd();
  } else if (endpoints && endpoints.webChat) {
    baseUrl = endpoints.webChat;
  }
  return baseUrl;
};
/**
 * Injects a method to create an instance of web chat into the props for the component.
 *
 * withWebChatCustomBaseUrl is a higher order component that takes a component as an argument.
 *
 * withWebChat is an external component that takes a config object as an argument and then returns a function that
 * takes a component as an argument. The config argument contains two parameters. First, a "debug" option that takes a
 * boolean. When turned on, this will add console.logs outlying the status of the web chat during each step of build
 * and tear down. The second parameter is baseUrl. This is used to identify where the web chat should load from.
 * config.baseUrl is automatically set using feature flags and environment variables, so you shouldn't have to touch
 * that normally.
 */
export const withWebChatCustomBaseUrl = WrappedComponent => {
  const config = {
    baseUrl: getWebchatBaseURL(),
    debug: false
  };
  return withWebChat(config)(WrappedComponent);
};

/**
 * If web chat is defined, load it.
 * @param {string} csrfToken - The CSRF token to include as a custom header
 * @param {object} element - specify DOM container to hold the web chat
 * @param {string} integrationId – web chat config
 * @param {object} integration – web chat config
 * @param {string} namespace - unique namespace to allow multiple web chats on the tooling site
 * @param {string} plan – to define subscriptionID
 * @param {string} premiumPlanIntegrationOrgId – to define subscriptionID
 * @param {string} region – web chat config
 * @param {string} serviceInstanceID – web chat config
 * @param {string} toolingType - One of TOOLING_TYPES
 * @param {bool} shouldTakeFocusIfOpensAutomatically - If this web chat should automatically try to grab focus
 * @param {func} additionalOnLoad - If you want to add some more onLoad methods than the default.
 * @param {bool} shouldAutoRender - If the web chat should automatically call instance.render.
 * @param {bool} isDesktopLauncherPreview - If we are previewing the desktop launcher.
 * @param {bool} isMobileLauncherPreview - If we are previewing the mobile launcher.
 * @param {bool} disableSessionHistory - If we should disable session history.
 * @return {object} web chat config object for <WebChat config={config} />
 */
export const getWebChatTabsBaseWebChatConfig = async (
  csrfToken,
  element,
  integrationId,
  integration,
  namespace,
  plan,
  premiumPlanIntegrationOrgId,
  region,
  serviceInstanceID,
  toolingType,
  shouldTakeFocusIfOpensAutomatically,
  additionalOnLoad,
  shouldAutoRender,
  isDesktopLauncherPreview,
  isMobileLauncherPreview,
  disableSessionHistory
) => {
  // Premium plans require a subscriptionID
  const subscriptionID =
    (plan === PlanName.PREMIUM ||
      plan === PlanName.ENTERPRISE_DATA_ISOLATION) &&
    premiumPlanIntegrationOrgId
      ? premiumPlanIntegrationOrgId
      : undefined;
  const isLauncherOnly = isDesktopLauncherPreview || isMobileLauncherPreview;

  const baseConfig = {
    integrationID: integrationId,
    namespace,
    region,
    serviceInstanceID,
    subscriptionID,
    disableSessionHistory,
    orchestrateUIAgentExtensions: isWxoEnabled,
    onLoad: async instance => {
      // We are going to start caching config values fetched by Web chat, so we want to make sure we populate
      // with the non cached values here.

      // Update the bot name.
      instance.updateBotName(
        (integration.config &&
          integration.config.public &&
          integration.config.public.botName) ||
          namespace
      );

      if (additionalOnLoad && typeof additionalOnLoad === 'function') {
        additionalOnLoad(instance);
      }

      if (shouldAutoRender) {
        await instance.render();
      }
    },
    hideCloseButton: true,
    __ibm__: {
      toolingType,
      allowPrivateMethods: true,
      /**
       * We must specify the CSRF token as a custom header
       * so that the server correctly validates the message call.
       * We don't need it in the config call
       * because GET calls are not validated by the CSRF token.
       */
      messageHeaders: {
        [Header.X_CSRF_TOKEN]: csrfToken
      }
    }
  };

  // If we are viewing the assistant preview or web chat preview, show a warning when connect to agent is hit.
  if (
    toolingType === TOOLING_TYPES.ASSISTANT_PREVIEW ||
    toolingType === TOOLING_TYPES.WEB_CHAT
  ) {
    baseConfig.__ibm__.connectToAgentBehavior =
      CONNECT_TO_AGENT_BEHAVIOR_TYPES.WARN_MISSING;
  }

  if (!isLauncherOnly) {
    baseConfig.element = element;
    baseConfig.disableCustomElementMobileEnhancements = true;
    baseConfig.showLauncher = false;
    baseConfig.openChatByDefault = true;
    baseConfig.shouldTakeFocusIfOpensAutomatically =
      shouldTakeFocusIfOpensAutomatically;
  }

  if (isDesktopLauncherPreview) {
    baseConfig.__ibm__.launcherToolingPreview = 'desktop';
    baseConfig.namespace = baseConfig.namespace + '-desktop-launcher';
    baseConfig.openChatByDefault = false;
    baseConfig.showLauncher = true;
  }

  if (isMobileLauncherPreview) {
    baseConfig.__ibm__.launcherToolingPreview = 'mobile';
    baseConfig.namespace = baseConfig.namespace + '-mobile-launcher';
    baseConfig.openChatByDefault = false;
    baseConfig.showLauncher = true;
  }

  if (isCpd) {
    baseConfig.cloudPrivateHostURL = await buildIntegrationsUrlCpd();
  }

  // If wxo is enabled and we are either on CPD or we are on AWS CIO/Premium-like,
  // we need to tell the WXO forms where to POST to.
  if (isWxoEnabled && (isCpd || (isAws && baseConfig.subscriptionID))) {
    baseConfig.orchestratePrivateHostURL = wxoUrl;
  }

  baseConfig.__ibm__.customMessageURL = await getCustomMessageURL(
    integrationId
  );
  baseConfig.__ibm__.customConfigURL = await getCustomConfigURL(integrationId);

  return baseConfig;
};

/**
 * Updates an existing web chat instance based on the given config.
 * @param {object} instance – The web chat instance
 * @param {object} config - The web chat configuration
 * @param {object} previousConfig - The previous web chat configuration
 */
export const updateWebchatInstanceWithConfig = async (
  instance,
  config,
  previousConfig
) => {
  if (!instance || !config) {
    return;
  }

  // IMPORTANT: Because of the way updateThemeConfig() has been set up in web chat, this needs to be called before
  // updateCSSVariables. Otherwise updateThemeConfig() will over write any styling variables that are provided in
  // updateCSSVariables and instead use the variables that were provided with the original web chat configs. This is a
  // known issue in web chat however given that updateThemeConfig() is a private web chat method this is not a priority
  // to fix at this time.
  if (
    config?.theme_config &&
    typeof instance.updateThemeConfig === 'function'
  ) {
    instance.updateThemeConfig(config?.theme_config);
  }

  if (config?.css_variables) {
    const areColorsValid = Object.values(config?.css_variables)?.every(color =>
      COLOR_REGEX.test(color)
    );

    if (areColorsValid) {
      instance.updateCSSVariables(
        null,
        JSON.parse(JSON.stringify(config?.css_variables))
      );
    }
  }

  if (config?.botName) {
    instance.updateBotName(config?.botName);
  }

  if (config?.botAvatarURL) {
    instance.updateBotAvatarURL(config?.botAvatarURL);
  }

  if (config?.launcher_config && instance.updateLauncherConfig) {
    instance.updateLauncherConfig(config?.launcher_config);
  }

  if (config?.home_screen) {
    instance.updateHomeScreenConfig(config?.home_screen);
    // If home screen changes, we need to restart the web chat.
    if (previousConfig?.home_screen?.is_on !== config.home_screen.is_on) {
      await instance.restartConversation();
    }
  }

  if (config?.branding_config) {
    instance.updateBrandingConfig(config?.branding_config);
  }

  if (
    config?.streaming &&
    typeof instance.updateStreamingConfig === 'function'
  ) {
    instance.updateStreamingConfig(config?.streaming);
  }

  if (config?.alternate_suggestions) {
    instance.updateAlternateSuggestionsConfig(config?.alternate_suggestions);
  }
};

export function populateInitialConfig(
  accountOwnerDomain = '',
  name = '',
  botName = '',
  recentEditorDomain = '',
  plan = ''
) {
  return {
    name,
    description: '',
    type: WEBCHAT_CHANNEL_TYPE,
    settings: settingsSchema.cast({
      accountOwnerDomain,
      botName,
      recentEditorDomain,
      plan
    })
  };
}
